import React, { useMemo } from "react";
import * as THREE from "three";

// ============================================================================
// 🐾 ZUI MESH (CHAMPION SPECIFICATION - ARENA WINNER a001)
// Marshmallow-white fluffy cat/bunny hybrid pet with procedural anime eyes
// ============================================================================

export interface ZuiProps {
  frame: number;
  fps: number;
  isSpeaking?: boolean;
}

export const ZuiMesh: React.FC<ZuiProps> = ({ frame, fps, isSpeaking = false }) => {
  const t = frame / fps; // Deterministic time in seconds (pure Remotion compliance)

  // --- DETERMINISTIC ANIMATIONS ---
  // Breathing bob & subtle scale squash/stretch
  const breatheY = Math.sin(t * 2.4) * 0.035;
  const breatheScaleY = 1 + Math.sin(t * 2.4) * 0.018;
  const breatheScaleXZ = 1 - Math.sin(t * 2.4) * 0.009;

  // Procedural Blink: quick natural blink every 3.2 seconds
  const blinkCycle = t % 3.2;
  const isBlinking = blinkCycle > 3.05 && blinkCycle < 3.2;
  const eyeScaleY = isBlinking ? 0.08 : 1.0;

  // Ear Twitching: independent procedural twitches
  const earCycleL = t % 2.7;
  const earTwitchL = earCycleL < 0.22 ? Math.sin((earCycleL / 0.22) * Math.PI) * 0.22 : 0;
  const earCycleR = (t + 1.2) % 3.1;
  const earTwitchR = earCycleR < 0.2 ? Math.sin((earCycleR / 0.2) * Math.PI) * -0.18 : 0;

  // Fluffy Tail Wagging: fluid sinusoidal wave
  const tailWagBase = Math.sin(t * 4.8) * 0.38;
  const tailWagTip = Math.sin(t * 4.8 + 0.9) * 0.55;

  // Speaking mouth movement
  const mouthOpen = isSpeaking ? 0.02 + Math.abs(Math.sin(t * 12.5)) * 0.055 : 0.008;

  // Arm gestures (gentle welcoming wave on right arm)
  const waveRight = Math.sin(t * 3.2) * 0.32;
  const restLeft = Math.sin(t * 2.4) * 0.06;

  // --- PBR MATERIALS (MEMOIZED FOR ZERO GC JANK) ---
  const furMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#FFFBF5", // Ultra soft warm marshmallow
    roughness: 0.68,
    metalness: 0.02,
  }), []);

  const bellyMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#FFEAD8", // Soft peach belly patch
    roughness: 0.75,
    metalness: 0.0,
  }), []);

  const innerEarMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#FFA8BA", // Blossom pink
    roughness: 0.55,
    metalness: 0.0,
  }), []);

  const eyeGlossMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#120B1E", // Obsidian anime pupil
    roughness: 0.04,
    metalness: 0.2,
  }), []);

  const eyeHighlightMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#FFFFFF",
    roughness: 0.02,
    metalness: 0.1,
    emissive: new THREE.Color("#FFFFFF"),
    emissiveIntensity: 0.6,
  }), []);

  const cheekBlushMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#FF6584",
    roughness: 0.8,
    metalness: 0.0,
    emissive: new THREE.Color("#FF3366"),
    emissiveIntensity: 0.25,
  }), []);

  const noseMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#FF8FA3",
    roughness: 0.35,
    metalness: 0.05,
  }), []);

  const mouthMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#E85A7A",
    roughness: 0.45,
    metalness: 0.0,
  }), []);

  const collarMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#00E5FF", // Signature Electric Cyan Collar
    roughness: 0.18,
    metalness: 0.5,
    emissive: new THREE.Color("#00E5FF"),
    emissiveIntensity: 0.45,
  }), []);

  const bellGoldMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#FFD700", // Polished Gold Bell
    roughness: 0.12,
    metalness: 0.92,
  }), []);

  return (
    <group position={[0, breatheY, 0]}>
      {/* === BODY WITH SQUASH & STRETCH === */}
      <group scale={[breatheScaleXZ, breatheScaleY, breatheScaleXZ]}>
        <mesh material={furMaterial} castShadow receiveShadow>
          <sphereGeometry args={[0.54, 28, 22]} />
        </mesh>
        {/* Soft Peach Belly */}
        <mesh material={bellyMaterial} position={[0, -0.06, 0.32]} scale={[0.88, 0.82, 0.45]} castShadow>
          <sphereGeometry args={[0.34, 22, 16]} />
        </mesh>
      </group>

      {/* === HEAD PIVOT (CONNECTED AT NECK) === */}
      <group position={[0, 0.54, 0.04]}>
        <mesh material={furMaterial} castShadow receiveShadow>
          <sphereGeometry args={[0.4, 28, 22]} />
        </mesh>

        {/* Fluffy Cheeks */}
        <mesh material={furMaterial} position={[-0.24, -0.06, 0.22]} scale={[0.9, 0.7, 0.7]} castShadow>
          <sphereGeometry args={[0.14, 16, 12]} />
        </mesh>
        <mesh material={furMaterial} position={[0.24, -0.06, 0.22]} scale={[0.9, 0.7, 0.7]} castShadow>
          <sphereGeometry args={[0.14, 16, 12]} />
        </mesh>

        {/* Soft Cheek Blush */}
        <mesh material={cheekBlushMaterial} position={[-0.23, -0.03, 0.28]} scale={[0.09, 0.05, 0.04]} />
        <mesh material={cheekBlushMaterial} position={[0.23, -0.03, 0.28]} scale={[0.09, 0.05, 0.04]} />

        {/* Cute Snout & Nose */}
        <mesh material={furMaterial} position={[0, -0.06, 0.32]} scale={[1.1, 0.8, 0.8]} castShadow>
          <sphereGeometry args={[0.11, 14, 12]} />
        </mesh>
        <mesh material={noseMaterial} position={[0, -0.02, 0.41]} castShadow>
          <sphereGeometry args={[0.032, 12, 10]} />
        </mesh>

        {/* Speaking Mouth */}
        <mesh material={mouthMaterial} position={[0, -0.09, 0.37]} scale={[1.0, mouthOpen > 0.015 ? 1.4 : 0.4, 0.8]}>
          <sphereGeometry args={[0.045, 12, 10]} />
        </mesh>

        {/* --- DUAL 3D ANIME EYES WITH SPECULAR HIGHLIGHTS --- */}
        {([-1, 1] as const).map((side) => (
          <group key={`zui-eye-${side}`} position={[side * 0.14, 0.06, 0.31]}>
            {/* Glossy Sclera & Iris */}
            <mesh material={eyeGlossMaterial} scale={[1, eyeScaleY, 1]} castShadow>
              <sphereGeometry args={[0.082, 18, 16]} />
            </mesh>
            {/* Primary Top-Right Specular Highlight (3D Anime Look) */}
            <mesh
              material={eyeHighlightMaterial}
              position={[side * 0.022, 0.03, 0.065]}
              scale={[1, eyeScaleY, 1]}
            >
              <sphereGeometry args={[0.022, 10, 8]} />
            </mesh>
            {/* Secondary Bottom-Left Micro Highlight */}
            <mesh
              material={eyeHighlightMaterial}
              position={[side * -0.018, -0.022, 0.062]}
              scale={[0.55, eyeScaleY * 0.55, 0.55]}
            >
              <sphereGeometry args={[0.018, 8, 8]} />
            </mesh>
          </group>
        ))}

        {/* --- EARS (SOFT CHIBI CAT/BUNNY HYBRID WITH ROUNDED VOLUME) --- */}
        {/* Left Ear */}
        <group position={[-0.26, 0.38, -0.02]} rotation={[0.08, 0.12, 0.28 + earTwitchL]}>
          {/* Outer Ear Mesh */}
          <mesh material={furMaterial} scale={[0.16, 0.32, 0.12]} castShadow>
            <coneGeometry args={[1, 1.35, 24]} />
          </mesh>
          {/* Inner Ear Blossom Pink Soft Inset */}
          <mesh material={innerEarMaterial} position={[0, -0.015, 0.038]} scale={[0.11, 0.24, 0.06]}>
            <coneGeometry args={[1, 1.15, 24]} />
          </mesh>
        </group>
        {/* Right Ear */}
        <group position={[0.26, 0.38, -0.02]} rotation={[0.08, -0.12, -0.28 - earTwitchR]}>
          {/* Outer Ear Mesh */}
          <mesh material={furMaterial} scale={[0.16, 0.32, 0.12]} castShadow>
            <coneGeometry args={[1, 1.35, 24]} />
          </mesh>
          {/* Inner Ear Blossom Pink Soft Inset */}
          <mesh material={innerEarMaterial} position={[0, -0.015, 0.038]} scale={[0.11, 0.24, 0.06]}>
            <coneGeometry args={[1, 1.15, 24]} />
          </mesh>
        </group>
      </group>

      {/* === SIGNATURE ELECTRIC CYAN COLLAR & GOLD BELL === */}
      <group position={[0, 0.27, 0.05]}>
        <mesh material={collarMaterial} castShadow>
          <torusGeometry args={[0.28, 0.038, 12, 28]} />
        </mesh>
        {/* Polished Bell */}
        <mesh material={bellGoldMaterial} position={[0, -0.08, 0.27]} castShadow>
          <sphereGeometry args={[0.06, 16, 14]} />
        </mesh>
      </group>

      {/* === ARMS & WELCOMING GESTURES === */}
      {/* Left Arm (Resting / Balance) */}
      <group position={[-0.42, 0.1, 0.1]} rotation={[0.2, 0, -0.3 + restLeft]}>
        <mesh material={furMaterial} castShadow>
          <capsuleGeometry args={[0.065, 0.16, 8, 12]} />
        </mesh>
      </group>
      {/* Right Arm (Waving Welcome Gesture) */}
      <group position={[0.42, 0.12, 0.1]} rotation={[0.4 + waveRight, 0, 0.4 + waveRight * 0.8]}>
        <mesh material={furMaterial} castShadow>
          <capsuleGeometry args={[0.065, 0.16, 8, 12]} />
        </mesh>
      </group>

      {/* === FEET === */}
      <mesh material={furMaterial} position={[-0.22, -0.48, 0.15]} scale={[1.1, 0.7, 1.4]} castShadow receiveShadow>
        <sphereGeometry args={[0.12, 14, 12]} />
      </mesh>
      <mesh material={furMaterial} position={[0.22, -0.48, 0.15]} scale={[1.1, 0.7, 1.4]} castShadow receiveShadow>
        <sphereGeometry args={[0.12, 14, 12]} />
      </mesh>

      {/* === FLUFFY PROCEDURAL WAGGING TAIL === */}
      <group position={[0, -0.22, -0.48]} rotation={[-0.4, tailWagBase, 0]}>
        <mesh material={furMaterial} position={[0, 0.08, -0.1]} castShadow>
          <sphereGeometry args={[0.09, 12, 10]} />
        </mesh>
        <group position={[0, 0.18, -0.16]} rotation={[0, tailWagTip, 0]}>
          <mesh material={furMaterial} castShadow>
            <sphereGeometry args={[0.11, 14, 12]} />
          </mesh>
          <mesh material={bellyMaterial} position={[0, 0.06, -0.06]} scale={[0.8, 0.8, 0.8]}>
            <sphereGeometry args={[0.08, 12, 10]} />
          </mesh>
        </group>
      </group>
    </group>
  );
};


// ============================================================================
// 🔮 BOLO MESH (CHAMPION SPECIFICATION - ARENA COMPANION)
// Lavender/violet round sphere companion with spring antenna & shocked dynamics
// ============================================================================

export interface BoloProps {
  frame: number;
  fps: number;
  isSpeaking?: boolean;
  isShocked?: boolean;
}

export const BoloMesh: React.FC<BoloProps> = ({
  frame,
  fps,
  isSpeaking = false,
  isShocked = false,
}) => {
  const t = frame / fps;

  // --- DETERMINISTIC DYNAMICS ---
  const breatheY = Math.sin(t * 2.1) * 0.028;

  // Shocked State Scaling: instantly squashes/stretches and expands eyes
  const shockExpansion = isShocked ? 1.22 : 1.0;
  const shockEyeScale = isShocked ? 1.48 : 1.0;
  const shockJitter = isShocked ? Math.sin(t * 45) * 0.015 : 0;

  // Flexible Antenna Multi-Segment Spring Physics (Deterministic Euler-Harmonics)
  const baseSwayX = Math.sin(t * 2.8) * 0.18 + shockJitter * 2;
  const baseSwayZ = Math.cos(t * 2.1) * 0.14;
  const midSwayX = Math.sin(t * 3.6 + 0.6) * 0.28;
  const midSwayZ = Math.cos(t * 2.9 + 0.6) * 0.22;
  const tipSwayX = Math.sin(t * 4.4 + 1.2) * 0.42;
  const tipSwayZ = Math.cos(t * 3.7 + 1.2) * 0.34;

  // Blink animation
  const blinkCycle = (t + 1.5) % 3.6;
  const isBlinking = blinkCycle > 3.45 && blinkCycle < 3.6;
  const eyeScaleY = isBlinking ? 0.08 : shockEyeScale;

  // Speaking mouth movement
  const mouthOpen = isSpeaking
    ? 0.02 + Math.abs(Math.sin(t * 11.5)) * 0.06
    : isShocked
    ? 0.075 // Shocked dropped jaw
    : 0.008;

  // Flapping chibi hands
  const handFlapL = Math.sin(t * 2.8) * 0.22 + (isShocked ? Math.sin(t * 25) * 0.25 : 0);
  const handFlapR = Math.cos(t * 2.8) * 0.22 + (isShocked ? Math.cos(t * 25) * 0.25 : 0);

  // --- PBR MATERIALS ---
  const bodyMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#BA78F8", // Rich soft lavender/violet
    roughness: 0.32,
    metalness: 0.08,
  }), []);

  const bellyMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#F5EDFF", // Clean cream belly
    roughness: 0.45,
    metalness: 0.02,
  }), []);

  const cheekMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#FF477E",
    roughness: 0.4,
    metalness: 0.0,
    emissive: new THREE.Color("#FF0055"),
    emissiveIntensity: 0.35,
  }), []);

  const eyeGlossMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#180A2E",
    roughness: 0.05,
    metalness: 0.25,
  }), []);

  const highlightMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#FFFFFF",
    roughness: 0.02,
    metalness: 0.0,
    emissive: new THREE.Color("#FFFFFF"),
    emissiveIntensity: 0.5,
  }), []);

  const mouthMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#D946EF",
    emissive: new THREE.Color("#9333EA"),
    emissiveIntensity: 0.2,
    roughness: 0.3,
  }), []);

  const antennaBulbMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: "#F472B6", // Glowing pink bulb on antenna tip
    roughness: 0.15,
    metalness: 0.2,
    emissive: new THREE.Color("#F472B6"),
    emissiveIntensity: 0.7,
  }), []);

  return (
    <group position={[0, breatheY + shockJitter, 0]} scale={[shockExpansion, shockExpansion, shockExpansion]}>
      {/* === ROUND BODY SPHERE === */}
      <mesh material={bodyMaterial} castShadow receiveShadow>
        <sphereGeometry args={[0.56, 32, 26]} />
      </mesh>

      {/* Soft Cream Belly */}
      <mesh material={bellyMaterial} position={[0, -0.08, 0.35]} scale={[0.85, 0.8, 0.42]} castShadow>
        <sphereGeometry args={[0.34, 24, 18]} />
      </mesh>

      {/* Rosy Cheek Blush */}
      <mesh material={cheekMaterial} position={[-0.26, -0.04, 0.42]} scale={[0.1, 0.06, 0.04]} />
      <mesh material={cheekMaterial} position={[0.26, -0.04, 0.42]} scale={[0.1, 0.06, 0.04]} />

      {/* --- BIG CARTOON EXPRESSIVE EYES --- */}
      {([-1, 1] as const).map((side) => (
        <group key={`bolo-eye-${side}`} position={[side * 0.16, 0.08, 0.46]}>
          {/* Main Iris/Pupil */}
          <mesh material={eyeGlossMaterial} scale={[shockEyeScale, eyeScaleY, shockEyeScale]} castShadow>
            <sphereGeometry args={[0.09, 20, 16]} />
          </mesh>
          {/* Specular Highlight */}
          <mesh
            material={highlightMaterial}
            position={[side * 0.024, 0.035, 0.075]}
            scale={[1, eyeScaleY > 0.2 ? 1 : 0.1, 1]}
          >
            <sphereGeometry args={[0.026, 10, 8]} />
          </mesh>
        </group>
      ))}

      {/* Speaking / Shocked Mouth */}
      <mesh material={mouthMaterial} position={[0, -0.1, 0.51]} scale={[1.1, mouthOpen > 0.02 ? 1.8 : 0.4, 0.6]}>
        <sphereGeometry args={[0.045, 14, 10]} />
      </mesh>

      {/* --- MULTI-SEGMENT PHYSICS ANTENNA --- */}
      <group position={[0, 0.54, 0]}>
        {/* Antenna Base Stem */}
        <group rotation={[baseSwayX, 0, baseSwayZ]}>
          <mesh material={bodyMaterial} castShadow>
            <cylinderGeometry args={[0.022, 0.03, 0.16, 12]} />
          </mesh>
          {/* Mid Joint */}
          <group position={[0, 0.14, 0]} rotation={[midSwayX, 0, midSwayZ]}>
            <mesh material={bodyMaterial} castShadow>
              <cylinderGeometry args={[0.016, 0.022, 0.16, 12]} />
            </mesh>
            {/* Tip Joint & Glowing Bulb */}
            <group position={[0, 0.14, 0]} rotation={[tipSwayX, 0, tipSwayZ]}>
              <mesh material={antennaBulbMaterial} castShadow>
                <sphereGeometry args={[0.068, 18, 16]} />
              </mesh>
            </group>
          </group>
        </group>
      </group>

      {/* --- FLAPPING CHIBI HANDS --- */}
      <group position={[-0.52, -0.04, 0.12]} rotation={[0, 0, -0.4 + handFlapL]}>
        <mesh material={bodyMaterial} scale={[0.8, 0.5, 1.2]} castShadow>
          <sphereGeometry args={[0.095, 14, 12]} />
        </mesh>
      </group>
      <group position={[0.52, -0.04, 0.12]} rotation={[0, 0, 0.4 + handFlapR]}>
        <mesh material={bodyMaterial} scale={[0.8, 0.5, 1.2]} castShadow>
          <sphereGeometry args={[0.095, 14, 12]} />
        </mesh>
      </group>

      {/* --- ROUND FEET (GROUNDED) --- */}
      <mesh material={bodyMaterial} position={[-0.2, -0.52, 0.15]} scale={[1.1, 0.65, 1.4]} castShadow receiveShadow>
        <sphereGeometry args={[0.13, 14, 12]} />
      </mesh>
      <mesh material={bodyMaterial} position={[0.2, -0.52, 0.15]} scale={[1.1, 0.65, 1.4]} castShadow receiveShadow>
        <sphereGeometry args={[0.13, 14, 12]} />
      </mesh>
    </group>
  );
};
